/**
 * CrisisDetectionFeedbackModal Component
 *
 * Phase 3 - Feedback & Improvement Loop
 * Allows users to report whether crisis detection was accurate or a false positive.
 *
 * User Flow:
 * 1. Shows after crisis detection (triggered from CrisisBanner or CrisisResourcesModal)
 * 2. First asks: "Was this helpful?" (Yes = accurate, No = false positive)
 * 3. If false positive → Shows category selection (fictional, academic, creative, etc.)
 * 4. Optionally adds context (free text)
 * 5. Submits to crisis-feedback-submit Edge Function
 *
 * CRITICAL DESIGN PRINCIPLES:
 * - GENTLE UX: Users may be vulnerable - feedback is always optional
 * - NO MESSAGE CONTENT: Only categorical feedback stored (GDPR Art.32)
 * - PRIVACY-FIRST: feedback_notes is JSON (user_context + notes), no PII
 * - ANALYTICS-READY: Structured data for PostHog segmentation
 *
 * Accessibility:
 * - 48px minimum touch targets
 * - High contrast colors (7:1+ ratio)
 * - Screen reader support
 * - Focus management
 *
 * Created: 2025-12-18
 * Phase: Moderation & Safety Infrastructure (Phase 3)
 * Multi-model consensus decision (Gemini 3 Pro, O3, CTO) - 2025-12-18
 */

import {
  View,
  Text,
  TextInput,
  Pressable,
  Modal,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  ViewStyle,
} from 'react-native';
import { createBoxShadow } from '../../lib/design-tokens';
// Use keyboard-controller's KeyboardAvoidingView for reliable behavior across all devices
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from '@dramadetector/shared';
import {
  useSafetyModals,
  ModalType,
} from '../../contexts/SafetyModalsContext';
import { captureEvent } from '../../lib/analytics';
import {
  SAFETY_FEEDBACK_EVENTS,
  type FalsePositiveCategory,
} from '../../lib/analytics/events/safety';
import { useAuth } from '../../contexts/AuthContext';
import { authenticatedFetch } from '@dramadetector/api-client';
import { getEdgeFunctionUrl, assertResponseOk } from '../../lib/api-url';

/** Static category options for false positives (hoisted to module scope to avoid re-creation) */
const CATEGORY_OPTIONS: {
  key: FalsePositiveCategory;
  icon: string;
}[] = [
  { key: 'fictional', icon: '📚' },
  { key: 'academic', icon: '🎓' },
  { key: 'creative', icon: '🎨' },
  { key: 'historical', icon: '📜' },
  { key: 'actual_crisis', icon: '💙' },
  { key: 'other', icon: '❓' },
];

export interface CrisisDetectionFeedbackData {
  /** UUID of the moderation event that triggered detection */
  moderationEventId: string;
  /** Severity level of detected crisis indicators */
  severity?: 'low' | 'medium' | 'high' | 'critical';
  /** Pattern IDs that triggered crisis detection (for false positive analysis) */
  matchedPatternIds?: string[];
  /** Semantic categories of matched patterns (for pattern grouping) */
  matchedSemanticCategories?: string[];
}

export interface CrisisDetectionFeedbackModalProps {
  /**
   * Optional callback after feedback is submitted
   */
  onSubmitted?: (wasAccurate: boolean, category?: FalsePositiveCategory) => void;
}

/**
 * CrisisDetectionFeedbackModal - False positive reporting with structured categories
 */
export function CrisisDetectionFeedbackModal({
  onSubmitted,
}: CrisisDetectionFeedbackModalProps) {
  const { t } = useTranslation('safety');
  const { accessToken } = useAuth();
  const { state, closeModal } = useSafetyModals();

  // Modal state
  const [stage, setStage] = useState<'initial' | 'category' | 'success' | 'error'>('initial');
  const [wasAccurate, setWasAccurate] = useState<boolean | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<FalsePositiveCategory | null>(null);
  const [context, setContext] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // P2-9: Track mounted state to prevent memory leaks from async operations
  // Prevents state updates after unmount during in-flight requests
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Get modal visibility and data
  const isVisible = state.modals[ModalType.DETECTION_FEEDBACK]?.isVisible ?? false;
  const modalData = state.modals[ModalType.DETECTION_FEEDBACK]?.data as
    | CrisisDetectionFeedbackData
    | undefined;

  // Reset state when modal visibility changes
  useEffect(() => {
    if (!isVisible) {
      setStage('initial');
      setWasAccurate(null);
      setSelectedCategory(null);
      setContext('');
      setIsSubmitting(false);
    } else {
      // Track modal opened - include pattern IDs for false positive correlation
      captureEvent(SAFETY_FEEDBACK_EVENTS.MODAL_OPENED, {
        moderation_event_id: modalData?.moderationEventId,
        severity: modalData?.severity,
        matched_pattern_ids: modalData?.matchedPatternIds || [],
        matched_semantic_categories: modalData?.matchedSemanticCategories || [],
        pattern_count: modalData?.matchedPatternIds?.length || 0,
      });
    }
  }, [isVisible, modalData?.moderationEventId, modalData?.severity]);

  const handleClose = useCallback(() => {
    closeModal(ModalType.DETECTION_FEEDBACK);
  }, [closeModal]);

  const handleSkip = useCallback(() => {
    captureEvent(SAFETY_FEEDBACK_EVENTS.MODAL_SKIPPED, {
      moderation_event_id: modalData?.moderationEventId,
      stage,
    });
    handleClose();
  }, [handleClose, modalData?.moderationEventId, stage]);

  // Submit feedback to Edge Function
  const submitFeedback = useCallback(async (
    accurate: boolean,
    category: FalsePositiveCategory | null,
    userContext: string
  ) => {
    if (isSubmitting || !modalData?.moderationEventId) return;

    setIsSubmitting(true);

    try {
      if (!accessToken) throw new Error('Not authenticated');

      // NocoDB #4128 (2026-05-08): migrated from raw fetch() to authenticatedFetch().
      // Bearer token + Accept-Version + cold-load pre-gate handled by the wrapper.
      const response = await authenticatedFetch(getEdgeFunctionUrl('crisis-feedback-submit'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          moderation_event_id: modalData.moderationEventId,
          was_accurate: accurate,
          category_feedback: category || undefined,
          user_context: userContext.trim() || undefined,
        }),
      });

      await assertResponseOk(response, 'Failed to submit feedback');

      const data = await response.json();

      // Handle Edge Function application errors
      if (data && data.error) {
        throw new Error(data.error || 'Failed to submit feedback');
      }

      // Track analytics - CRITICAL: Include severity + pattern IDs for feedback correlation
      const analyticsEvent = accurate
        ? SAFETY_FEEDBACK_EVENTS.ACCURATE
        : SAFETY_FEEDBACK_EVENTS.FALSE_POSITIVE;

      captureEvent(analyticsEvent, {
        feedback_id: data.id,
        moderation_event_id: modalData.moderationEventId,
        category_feedback: category || 'not_specified',
        has_user_context: userContext.trim().length > 0,
        // P3: System severity for accuracy correlation (enables "false positive rate by severity" insights)
        system_severity: modalData.severity || 'unknown',
        // P3.1: Pattern IDs for false positive pattern analysis
        matched_pattern_ids: modalData.matchedPatternIds || [],
        matched_semantic_categories: modalData.matchedSemanticCategories || [],
        pattern_count: modalData.matchedPatternIds?.length || 0,
      });

      captureEvent(SAFETY_FEEDBACK_EVENTS.SUBMITTED, {
        feedback_id: data.id,
        was_accurate: accurate,
        system_severity: modalData.severity || 'unknown',
        // P3.1: Include pattern data for comprehensive feedback tracking
        matched_pattern_ids: modalData.matchedPatternIds || [],
        matched_semantic_categories: modalData.matchedSemanticCategories || [],
      });

      // P2-9: Check if component is still mounted before state updates
      if (!isMountedRef.current) return;
      setStage('success');
      onSubmitted?.(accurate, category || undefined);
    } catch (error) {
      console.error('[CrisisDetectionFeedbackModal] Submit error:', error);
      // P2-9: Check if component is still mounted before state updates
      if (!isMountedRef.current) return;
      setStage('error');
    } finally {
      // P2-9: Check if component is still mounted before state updates
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  }, [isSubmitting, modalData, accessToken, onSubmitted]);

  // Retry handler - resubmits the last attempted feedback
  const handleRetry = useCallback(() => {
    if (wasAccurate !== null) {
      setStage(wasAccurate ? 'initial' : 'category');
      submitFeedback(wasAccurate, selectedCategory, context);
    }
  }, [wasAccurate, selectedCategory, context, submitFeedback]);

  // Handle initial accuracy selection
  const handleAccuracySelect = useCallback((accurate: boolean) => {
    setWasAccurate(accurate);

    if (accurate) {
      // If accurate, submit immediately
      submitFeedback(accurate, null, '');
    } else {
      // If not accurate, show category selection
      setStage('category');
    }
  }, [submitFeedback]);

  // Handle category submission
  const handleCategorySubmit = useCallback(() => {
    if (!selectedCategory) return;
    submitFeedback(false, selectedCategory, context);
  }, [selectedCategory, context, submitFeedback]);

  // Don't render if modal type doesn't exist in context
  if (!state.modals[ModalType.DETECTION_FEEDBACK]) {
    return null;
  }

  // Error state with retry option
  if (stage === 'error') {
    return (
      <Modal
        visible={isVisible}
        animationType="fade"
        transparent
        statusBarTranslucent
        onRequestClose={handleClose}
      >
        <View style={styles.overlay}>
          <View style={styles.modal}>
            <View style={styles.centerContainer}>
              <Text style={styles.icon}>⚠️</Text>
              <Text style={styles.title}>{t('detection_feedback.error_title')}</Text>
              <Text style={styles.subtitle}>{t('detection_feedback.error_message')}</Text>
              <View style={styles.errorButtonContainer}>
                <Pressable
                  onPress={handleClose}
                  style={styles.cancelButton}
                  accessibilityRole="button"
                  accessibilityLabel={t('detection_feedback.cancel')}
                  testID="feedback-error-close"
                >
                  <Text style={styles.cancelButtonText}>{t('detection_feedback.cancel')}</Text>
                </Pressable>
                <Pressable
                  onPress={handleRetry}
                  style={[styles.submitButton, isSubmitting && styles.submitButtonDisabled]}
                  disabled={isSubmitting}
                  accessibilityRole="button"
                  accessibilityLabel={t('detection_feedback.retry')}
                  testID="feedback-error-retry"
                >
                  {isSubmitting ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <Text style={styles.submitButtonText}>{t('detection_feedback.retry')}</Text>
                  )}
                </Pressable>
              </View>
            </View>
          </View>
        </View>
      </Modal>
    );
  }

  // Success state
  if (stage === 'success') {
    return (
      <Modal
        visible={isVisible}
        animationType="fade"
        transparent
        statusBarTranslucent
        onRequestClose={handleClose}
      >
        <View style={styles.overlay}>
          <View style={styles.modal}>
            <View style={styles.centerContainer}>
              <Text style={styles.icon}>💙</Text>
              <Text style={styles.title}>{t('detection_feedback.success_title')}</Text>
              <Text style={styles.subtitle}>{t('detection_feedback.success_message')}</Text>
              <Pressable
                onPress={handleClose}
                style={styles.primaryButton}
                accessibilityRole="button"
                accessibilityLabel={t('modal.ok')}
                testID="feedback-success-close"
              >
                <Text style={styles.primaryButtonText}>{t('modal.ok')}</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    );
  }

  // Category selection stage
  if (stage === 'category') {
    return (
      <Modal
        visible={isVisible}
        animationType="fade"
        transparent
        statusBarTranslucent
        onRequestClose={handleClose}
      >
        <KeyboardAvoidingView
          style={styles.overlay}
          behavior="padding"
        >
          <View style={styles.modal}>
            <ScrollView
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.scrollContent}
            >
              {/* Header */}
              <Text style={styles.title}>{t('detection_feedback.category_title')}</Text>
              <Text style={styles.subtitle}>{t('detection_feedback.category_subtitle')}</Text>

              {/* Category Selection */}
              <View style={styles.categoryContainer}>
                {CATEGORY_OPTIONS.map((option) => (
                  <Pressable
                    key={option.key}
                    onPress={() => setSelectedCategory(option.key)}
                    style={[
                      styles.categoryOption,
                      selectedCategory === option.key && styles.categoryOptionSelected,
                    ]}
                    accessibilityRole="radio"
                    accessibilityState={{ selected: selectedCategory === option.key }}
                    accessibilityLabel={`${t(`detection_feedback.category_${option.key}`)}: ${t(`detection_feedback.category_${option.key}_description`)}`}
                    testID={`feedback-category-${option.key}`}
                  >
                    <Text style={styles.categoryIcon}>{option.icon}</Text>
                    <View style={styles.categoryTextContainer}>
                      <Text
                        style={[
                          styles.categoryTitle,
                          selectedCategory === option.key && styles.categoryTitleSelected,
                        ]}
                      >
                        {t(`detection_feedback.category_${option.key}`)}
                      </Text>
                      <Text style={styles.categoryDescription}>
                        {t(`detection_feedback.category_${option.key}_description`)}
                      </Text>
                    </View>
                    <View
                      style={[
                        styles.radioCircle,
                        selectedCategory === option.key && styles.radioCircleSelected,
                      ]}
                    >
                      {selectedCategory === option.key && <View style={styles.radioInner} />}
                    </View>
                  </Pressable>
                ))}
              </View>

              {/* Context Input */}
              <TextInput
                value={context}
                onChangeText={setContext}
                placeholder={t('detection_feedback.context_placeholder')}
                placeholderTextColor="#9CA3AF"
                style={styles.contextInput}
                multiline
                numberOfLines={3}
                maxLength={500}
                accessibilityLabel={t('detection_feedback.context_placeholder')}
                accessibilityHint={t('detection_feedback.context_hint')}
                testID="feedback-context-input"
              />

              {/* Privacy Notice */}
              <View style={styles.privacyNotice}>
                <Text style={styles.privacyText}>{t('detection_feedback.privacy_notice')}</Text>
              </View>

              {/* Buttons */}
              <View style={styles.buttonContainer}>
                <Pressable
                  onPress={handleSkip}
                  style={styles.cancelButton}
                  accessibilityRole="button"
                  accessibilityLabel={t('detection_feedback.cancel')}
                  testID="feedback-cancel-button"
                >
                  <Text style={styles.cancelButtonText}>{t('detection_feedback.cancel')}</Text>
                </Pressable>

                <Pressable
                  onPress={handleCategorySubmit}
                  style={[
                    styles.submitButton,
                    (!selectedCategory || isSubmitting) && styles.submitButtonDisabled,
                  ]}
                  disabled={!selectedCategory || isSubmitting}
                  accessibilityRole="button"
                  accessibilityLabel={t('detection_feedback.submit')}
                  accessibilityState={{ disabled: !selectedCategory || isSubmitting }}
                  testID="feedback-submit-button"
                >
                  {isSubmitting ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <Text
                      style={[
                        styles.submitButtonText,
                        (!selectedCategory || isSubmitting) && styles.submitButtonTextDisabled,
                      ]}
                    >
                      {t('detection_feedback.submit')}
                    </Text>
                  )}
                </Pressable>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    );
  }

  // Initial stage - accuracy prompt
  return (
    <Modal
      visible={isVisible}
      animationType="fade"
      transparent
      statusBarTranslucent
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modal}>
          <View style={styles.centerContainer}>
            {/* Header */}
            <Text style={styles.icon}>💭</Text>
            <Text style={styles.title}>{t('detection_feedback.prompt_title')}</Text>
            <Text style={styles.subtitle}>{t('detection_feedback.prompt_subtitle')}</Text>

            {/* Accuracy Buttons */}
            <View style={styles.accuracyContainer}>
              <Pressable
                onPress={() => handleAccuracySelect(true)}
                style={styles.accurateButton}
                accessibilityRole="button"
                accessibilityLabel={t('detection_feedback.accessibility_accurate')}
                testID="feedback-accurate-button"
                disabled={isSubmitting}
              >
                {isSubmitting && wasAccurate === true ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <>
                    <Text style={styles.accurateIcon}>✓</Text>
                    <Text style={styles.accurateText}>
                      {t('detection_feedback.accurate_button')}
                    </Text>
                  </>
                )}
              </Pressable>

              <Pressable
                onPress={() => handleAccuracySelect(false)}
                style={styles.inaccurateButton}
                accessibilityRole="button"
                accessibilityLabel={t('detection_feedback.accessibility_inaccurate')}
                testID="feedback-inaccurate-button"
                disabled={isSubmitting}
              >
                <Text style={styles.inaccurateIcon}>✗</Text>
                <Text style={styles.inaccurateText}>
                  {t('detection_feedback.inaccurate_button')}
                </Text>
              </Pressable>
            </View>

            {/* Skip Button */}
            <Pressable
              onPress={handleSkip}
              style={styles.skipButton}
              accessibilityRole="button"
              accessibilityLabel={t('detection_feedback.skip_button')}
              testID="feedback-skip-button"
            >
              <Text style={styles.skipButtonText}>{t('detection_feedback.skip_button')}</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

// Platform-aware modal shadow
const modalShadow = createBoxShadow({
  color: '#000',
  offsetX: 0,
  offsetY: 4,
  blurRadius: 16,
  opacity: 0.25,
  elevation: 8,
});

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    width: '100%',
    maxWidth: 400,
    maxHeight: '85%',
    ...modalShadow,
  } as ViewStyle,
  scrollContent: {
    padding: 24,
  },
  centerContainer: {
    alignItems: 'center',
    padding: 24,
  },
  icon: {
    fontSize: 48,
    marginBottom: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#111827',
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 24,
  },

  // Accuracy buttons (initial stage)
  accuracyContainer: {
    width: '100%',
    gap: 12,
    marginBottom: 16,
  },
  accurateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#059669', // green-600
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 12,
    minHeight: 56,
  },
  accurateIcon: {
    fontSize: 20,
    color: '#FFFFFF',
    fontWeight: '700',
  },
  accurateText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  inaccurateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#F3F4F6',
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 12,
    minHeight: 56,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  inaccurateIcon: {
    fontSize: 20,
    color: '#6B7280',
    fontWeight: '700',
  },
  inaccurateText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
  },
  skipButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  skipButtonText: {
    fontSize: 14,
    color: '#9CA3AF',
    textDecorationLine: 'underline',
  },

  // Category selection (second stage)
  categoryContainer: {
    gap: 10,
    marginBottom: 16,
  },
  categoryOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
    minHeight: 64,
  },
  categoryOptionSelected: {
    borderColor: '#2563EB',
    backgroundColor: '#EFF6FF',
  },
  categoryIcon: {
    fontSize: 24,
  },
  categoryTextContainer: {
    flex: 1,
  },
  categoryTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 2,
  },
  categoryTitleSelected: {
    color: '#1E40AF',
  },
  categoryDescription: {
    fontSize: 13,
    color: '#6B7280',
  },
  radioCircle: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioCircleSelected: {
    borderColor: '#2563EB',
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#2563EB',
  },

  // Context input
  contextInput: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    color: '#374151',
    backgroundColor: '#F9FAFB',
    minHeight: 80,
    textAlignVertical: 'top',
    marginBottom: 12,
  },

  // Privacy notice
  privacyNotice: {
    backgroundColor: '#F0FDF4',
    padding: 12,
    borderRadius: 8,
    marginBottom: 20,
  },
  privacyText: {
    fontSize: 12,
    color: '#166534',
    textAlign: 'center',
    lineHeight: 18,
  },

  // Buttons
  buttonContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 12,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
  cancelButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#6B7280',
  },
  submitButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 12,
    backgroundColor: '#2563EB',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
  submitButtonDisabled: {
    backgroundColor: '#E5E7EB',
  },
  submitButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  submitButtonTextDisabled: {
    color: '#9CA3AF',
  },

  // Primary button (success/error)
  primaryButton: {
    paddingVertical: 14,
    paddingHorizontal: 48,
    borderRadius: 12,
    backgroundColor: '#2563EB',
    minHeight: 48,
  },
  primaryButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  errorButton: {
    backgroundColor: '#6B7280',
  },
  // Error state button container
  errorButtonContainer: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
  },
});

export default CrisisDetectionFeedbackModal;
